import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Building, Users, Info } from 'lucide-react';
import { TeamAnalysisContext } from '@/types/teamAnalysisTypes';

interface TeamContextSelectorProps {
  context: Partial<Pick<TeamAnalysisContext, 'teamType' | 'companyPhase'>>;
  onContextChange: (context: Partial<Pick<TeamAnalysisContext, 'teamType' | 'companyPhase'>>) => void;
  disabled?: boolean;
}

const companyPhaseInfo: Record<string, string> = {
  Startup: 'Early stage: prioritizes speed, innovation, and adaptability over process',
  Growth: 'Scaling phase: focuses on culture alignment and team cohesion',
  Mature: 'Established: emphasizes stability, efficiency, and consistent performance'
};

const TeamContextSelector: React.FC<TeamContextSelectorProps> = ({
  context,
  onContextChange,
  disabled = false
}) => {
  const [companyPhaseOpen, setCompanyPhaseOpen] = useState(false);
  const teamTypes = ['Leadership', 'Sales', 'Marketing', 'Operations', 'Finance/Admin'];
  const companyPhases = ['Startup', 'Growth', 'Mature'];

  return (
    <Card className="border-0 shadow-lg mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Building className="h-5 w-5 text-primary" />
          Team Context
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Define your team's purpose and business context
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Team Type */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <Users className="h-4 w-4" />
              Team Type
            </label>
            <Select
              value={context.teamType || ''}
              onValueChange={(value) => onContextChange({ ...context, teamType: value as any })}
              disabled={disabled}
            >
              <SelectTrigger className="bg-background">
                <SelectValue placeholder="Select team type..." />
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                {teamTypes.map(type => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Company Phase */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <Building className="h-4 w-4" />
              Company Phase
            </label>
            <Select
              value={context.companyPhase || ''}
              onValueChange={(value) => onContextChange({ ...context, companyPhase: value as any })}
              disabled={disabled}
              open={companyPhaseOpen}
              onOpenChange={setCompanyPhaseOpen}
            >
              <SelectTrigger className="bg-background">
                <SelectValue placeholder="Select company phase..." />
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                {companyPhases.map(phase => (
                  <SelectItem key={phase} value={phase} className="relative pr-8">
                    {phase}
                    {companyPhaseOpen && (
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Info className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground hover:text-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent 
                          side="right" 
                          sideOffset={12}
                          className="z-[9999] max-w-[180px] text-xs"
                        >
                          {companyPhaseInfo[phase]}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Context Summary */}
        {(context.teamType || context.companyPhase) && (
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            {context.teamType && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                {context.teamType} Team
              </Badge>
            )}
            {context.companyPhase && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                {context.companyPhase} Phase
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamContextSelector;
